class HeaderJs extends HTMLElement {
  constructor() { super(); }

  connectedCallback() {
    this.headerContainer = this.querySelector('.header__container');
    this.initMobileMenu();
    this.initAccountDropdown();
    this.initLanguageSelector();
    this.observeTransparentZones();
    this.initScrollHide();
  }

  initMobileMenu() {
    this.menuButton = this.querySelector('.header__mobile-button');
    this.mobileMenu = this.querySelector('.header__menu-mobile__container');
    this.burgerIcon = this.querySelector('.icon-burger');
    this.crossIcon = this.querySelector('.icon-cross');

    if (!this.menuButton || !this.mobileMenu) return;

    this.menuButton.addEventListener('click', () => this.toggleMobileMenu());

    this.mobileMenu.querySelectorAll('.header__menu-item.has-submenu').forEach(item => {
      const link = item.querySelector('.header__menu-link');
      if (!link) return;
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const submenu = item.querySelector('.header__submenu');
        if (!submenu) return;
        const isOpen = item.classList.contains('is-open');
        this.mobileMenu.querySelectorAll('.header__menu-item.is-open').forEach(el => {
          el.classList.remove('is-open');
          el.querySelector('.header__submenu').style.setProperty('--submenu-h', '0px');
        });
        if (!isOpen) {
          item.classList.add('is-open');
          submenu.style.setProperty('--submenu-h', submenu.scrollHeight + 'px');
        }
      });
    });
  }

  toggleMobileMenu() {
    const headerSection = document.getElementById('shopify-section-header');
    const isOpen = this.mobileMenu.classList.toggle('open');
    if (isOpen && headerSection) headerSection.style.zIndex = '3000';
    this.mobileMenu.style.transform = isOpen ? 'translateX(0)' : '';
    this.burgerIcon.style.display = isOpen ? 'none' : 'flex';
    this.crossIcon.style.display = isOpen ? 'flex' : 'none';
    document.body.style.overflow = isOpen ? 'hidden' : '';
    if (!isOpen && headerSection) headerSection.style.zIndex = '';
  }

  initAccountDropdown() {
    this.accountWrapper = this.querySelector('.header__account-wrapper');
    this.accountBtn = this.querySelector('.header__account-button');

    if (!this.accountWrapper || !this.accountBtn) return;

    this.accountBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleAccountDropdown();
    });

    document.addEventListener('click', () => this.closeAccountDropdown());

    window.addEventListener('scroll', () => this.closeAccountDropdown(), { passive: true });
  }

  toggleAccountDropdown() {
    const isOpen = this.accountWrapper.classList.toggle('is-open');
    this.accountBtn.setAttribute('aria-expanded', String(isOpen));
    this.headerContainer.classList.toggle('has-open-dropdown', isOpen);
    if (isOpen) this.closeLanguageDropdown();
  }

  closeAccountDropdown() {
    if (!this.accountWrapper) return;
    this.accountWrapper.classList.remove('is-open');
    this.accountBtn.setAttribute('aria-expanded', 'false');
    this.headerContainer.classList.remove('has-open-dropdown');
  }

  initLanguageSelector() {
    this.langSelectors = Array.from(this.querySelectorAll('[data-weglot-selector]'));
    if (!this.langSelectors.length) return;

    this.langWrapper = this.querySelector('.language-selector--desktop');
    this.langToggle = this.langWrapper ? this.langWrapper.querySelector('.language-selector__toggle') : null;

    if (this.langToggle) {
      this.langToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleLanguageDropdown();
      });
      document.addEventListener('click', (e) => {
        if (!this.langWrapper.contains(e.target)) this.closeLanguageDropdown();
      });
      window.addEventListener('scroll', () => this.closeLanguageDropdown(), { passive: true });
    }

    this.waitForWeglot().then(() => this.populateWeglotSelectors());
  }

  waitForWeglot() {
    return new Promise((resolve) => {
      if (window.Weglot && window.Weglot.initialized) return resolve();
      const onReady = () => resolve();
      if (window.Weglot && typeof window.Weglot.on === 'function') {
        window.Weglot.on('initialized', onReady);
      }
      const poll = setInterval(() => {
        if (window.Weglot && window.Weglot.initialized) {
          clearInterval(poll);
          resolve();
        }
      }, 100);
      setTimeout(() => { clearInterval(poll); resolve(); }, 5000);
    });
  }

  getWeglotLanguages() {
    if (!window.Weglot) return [];
    const opts = window.Weglot.options || {};
    const source = {
      code: opts.language_from || 'fr',
      name: this.getEndonym(opts.language_from || 'fr')
    };
    const destinations = (opts.languages || []).map((l) => ({
      code: l.language_to,
      name: l.local_name || l.custom_name || this.getEndonym(l.language_to)
    }));
    return [source, ...destinations];
  }

  getEndonym(code) {
    const map = {
      fr: 'Français', en: 'English', es: 'Español', it: 'Italiano',
      de: 'Deutsch', pt: 'Português', nl: 'Nederlands', ja: '日本語'
    };
    return map[code] || code.toUpperCase();
  }

  populateWeglotSelectors() {
    const langs = this.getWeglotLanguages();
    if (!langs.length) return;
    const current = (window.Weglot && window.Weglot.getCurrentLang && window.Weglot.getCurrentLang()) || langs[0].code;

    this.langSelectors.forEach((selector) => {
      const variant = selector.dataset.weglotSelector;
      const list = selector.querySelector('[data-lang-list]');
      if (!list) return;
      list.innerHTML = '';

      langs.forEach((lang) => {
        const li = document.createElement('li');
        li.className = 'language-selector__item';
        if (variant === 'desktop') li.setAttribute('role', 'none');

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'language-selector__option p-small fw-500';
        if (lang.code === current) {
          btn.classList.add('is-active');
          btn.setAttribute('aria-current', 'true');
        }
        if (variant === 'desktop') {
          btn.setAttribute('role', 'menuitem');
          btn.innerHTML = `<span class="language-selector__option-name">${lang.name}</span><span class="language-selector__option-code">${lang.code.toUpperCase()}</span>`;
        } else {
          btn.setAttribute('aria-label', lang.name);
          btn.textContent = lang.code.toUpperCase();
        }
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          if (window.Weglot && typeof window.Weglot.switchTo === 'function') {
            window.Weglot.switchTo(lang.code);
          }
          this.closeLanguageDropdown();
        });

        li.appendChild(btn);
        list.appendChild(li);
      });

      selector.hidden = false;
    });

    const currentLabel = this.querySelector('[data-current-lang]');
    if (currentLabel) currentLabel.textContent = current.toUpperCase();

    if (window.Weglot && typeof window.Weglot.on === 'function') {
      window.Weglot.on('languageChanged', (newLang) => this.refreshActiveLanguage(newLang));
    }
  }

  refreshActiveLanguage(code) {
    const upper = code.toUpperCase();
    const currentLabel = this.querySelector('[data-current-lang]');
    if (currentLabel) currentLabel.textContent = upper;
    this.langSelectors.forEach((selector) => {
      selector.querySelectorAll('.language-selector__option').forEach((btn) => {
        const codeSpan = btn.querySelector('.language-selector__option-code');
        const btnCode = codeSpan ? codeSpan.textContent.trim() : btn.textContent.trim();
        const isActive = btnCode === upper;
        btn.classList.toggle('is-active', isActive);
        if (isActive) btn.setAttribute('aria-current', 'true');
        else btn.removeAttribute('aria-current');
      });
    });
  }

  toggleLanguageDropdown() {
    const isOpen = this.langWrapper.classList.toggle('is-open');
    this.langToggle.setAttribute('aria-expanded', String(isOpen));
    this.headerContainer.classList.toggle('has-open-dropdown', isOpen);
    if (isOpen) this.closeAccountDropdown();
  }

  closeLanguageDropdown() {
    if (!this.langWrapper) return;
    this.langWrapper.classList.remove('is-open');
    if (this.langToggle) this.langToggle.setAttribute('aria-expanded', 'false');
    if (!this.accountWrapper || !this.accountWrapper.classList.contains('is-open')) {
      this.headerContainer.classList.remove('has-open-dropdown');
    }
  }

  initScrollHide() {
    if (!this.headerContainer) return;

    const announcementBar = document.querySelector('.announcement-bar');
    const announcementHeight = announcementBar ? announcementBar.offsetHeight : 0;

    let lastScrollY = window.scrollY;
    let scrollDownAccum = 0;
    let ticking = false;

    const setDesktopHeight = () => {
      if (window.innerWidth >= 990) {
        document.documentElement.style.setProperty(
          '--header-h-desktop',
          this.headerContainer.offsetHeight + 'px'
        );
      }
    };
    setDesktopHeight();

    const updateStickyTop = () => {
      const isMobile = window.innerWidth < 990;
      if (!isMobile) {
        document.documentElement.style.removeProperty('--sticky-top');
        return;
      }

      const headerHeight = this.headerContainer.offsetHeight;
      const isHeaderHidden = this.headerContainer.classList.contains('is-hidden');

      let stickyTop = 0;
      if (!isHeaderHidden) {
        stickyTop = headerHeight + announcementHeight;
      }

      document.documentElement.style.setProperty('--sticky-top', `${stickyTop}px`);
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const delta = currentScrollY - lastScrollY;
          const isMobile = window.innerWidth < 990;

          this.headerContainer.classList.toggle('is-scrolled', currentScrollY > 10);

          const hideThreshold = isMobile ? announcementHeight + 50 : 200;

          if (currentScrollY > hideThreshold) {
            if (delta > 0) {
              scrollDownAccum += delta;
              if (scrollDownAccum > 100) {
                this.headerContainer.classList.add('is-hidden');
                document.body.classList.add('header-hidden');
              }
            } else if (delta < 0) {
              scrollDownAccum = 0;
              this.headerContainer.classList.remove('is-hidden');
              document.body.classList.remove('header-hidden');
            }
          } else {
            scrollDownAccum = 0;
            this.headerContainer.classList.remove('is-hidden');
            document.body.classList.remove('header-hidden');
          }

          updateStickyTop();
          lastScrollY = currentScrollY;
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', () => { updateStickyTop(); setDesktopHeight(); });
    updateStickyTop();
  }

  observeTransparentZones() {
    if (!this.headerContainer) return;

    const transparentSelectors = [
      '.hp__container',
      '.main-collection__container',
      '.main-register__container',
      '.hero__container',
      '.legals__hero',
      '.main-product-section'
    ];

    const zones = transparentSelectors
      .map(sel => document.querySelector(sel))
      .filter(Boolean);

    if (!zones.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        let isOverTransparentZone = false;

        entries.forEach(entry => {
          if (entry.isIntersecting) {
            isOverTransparentZone = true;
          }
        });

        this.headerContainer.classList.toggle(
          'is-transparent',
          isOverTransparentZone
        );
      },
      {
        root: null,
        rootMargin: `-${this.headerContainer.offsetHeight}px 0px 0px 0px`,
        threshold: 0,
      }
    );

    zones.forEach(zone => observer.observe(zone));
  }
}

if (!customElements.get('header-js')) {
  customElements.define('header-js', HeaderJs);
}
